import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clapperboard, Download, ImagePlus, Loader2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PRESET = "Slow cinematic drone push-in towards the building at golden hour, soft sunlight, gentle camera glide, premium real estate advertisement feel.";
const fileUrl = (id, kind = "video", dl = false) => `${BACKEND}/api/ai-videos/${id}/file?kind=${kind}${dl ? "&download=true" : ""}`;

const toJpeg = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const s = Math.min(1, 1280 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = () => reject(new Error("Could not read that image"));
  img.src = URL.createObjectURL(file);
});

const Pick = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5 min-w-0">
    <div className="label-caps">{label}</div>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-9 rounded-sm border-[#E6E4DD] bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const PhotoDrop = ({ image, setImage }) => {
  const ref = useRef();
  const onFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick a JPG or PNG photo"); return; }
    try { setImage(await toJpeg(f)); } catch (e) { toast.error(e.message); }
  };
  return (
    <button type="button" data-testid="ai-video-photo-btn" onClick={() => ref.current.click()}
      className="w-full aspect-video border border-dashed border-forest/30 rounded-sm bg-bone grid place-items-center overflow-hidden hover:border-forest transition-colors duration-150">
      {image ? <img src={image} alt="Project" data-testid="ai-video-photo-preview" className="w-full h-full object-cover" /> : (
        <div className="text-center text-forest/60 text-sm px-4"><ImagePlus className="h-6 w-6 mx-auto mb-2" />Upload a project photo (exterior, render or amenity)</div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid="ai-video-photo-input" onChange={(e) => onFile(e.target.files?.[0])} />
    </button>
  );
};

export const AiVideoForm = ({ onCreated }) => {
  const { projects } = useProjects();
  const [f, setF] = useState({ project: "", title: "", prompt: PRESET, duration: "5", aspect_ratio: "16:9", resolution: "720p" });
  const [image, setImage] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!image) { toast.error("Upload a project photo first"); return; }
    setBusy(true);
    try {
      const project_name = projects.find((p) => p.id === f.project)?.name || null;
      const { data } = await api.post("/ai-videos", { ...f, project_name, title: f.title || null, image }, { timeout: 60000 });
      toast.success("Video is being created — usually 1–3 minutes");
      setImage(""); setF((p) => ({ ...p, title: "" }));
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start the video"); }
    setBusy(false);
  };
  return (
    <div data-testid="ai-video-form" className="border border-[#E6E4DD] bg-white rounded-sm p-4 sm:p-6 space-y-4">
      <PhotoDrop image={image} setImage={setImage} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Pick label="Project (optional)" testId="ai-video-project" value={f.project || "__none__"} onChange={(v) => set("project")(v === "__none__" ? "" : v)}
          options={[["__none__", "No project"], ...projects.map((p) => [p.id, p.name])]} />
        <div className="space-y-1.5 min-w-0">
          <div className="label-caps">Title</div>
          <input data-testid="ai-video-title" value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="e.g. Cedar Grove launch teaser"
            className="w-full h-9 px-3 rounded-sm border border-[#E6E4DD] text-sm outline-none focus:border-forest" />
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="label-caps">Describe the motion</div>
        <textarea data-testid="ai-video-prompt" rows={3} value={f.prompt} onChange={(e) => set("prompt")(e.target.value)}
          className="w-full px-3 py-2 rounded-sm border border-[#E6E4DD] text-sm outline-none focus:border-forest resize-y" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Pick label="Length" testId="ai-video-duration" value={f.duration} onChange={set("duration")} options={[["5", "5 seconds"], ["10", "10 seconds"]]} />
        <Pick label="Shape" testId="ai-video-aspect" value={f.aspect_ratio} onChange={set("aspect_ratio")}
          options={[["16:9", "16:9 Wide"], ["9:16", "9:16 Reel"], ["1:1", "1:1 Square"], ["auto", "As photo"]]} />
        <Pick label="Quality" testId="ai-video-resolution" value={f.resolution} onChange={set("resolution")} options={[["720p", "HD 720p"], ["480p", "Draft 480p"]]} />
      </div>
      <button data-testid="ai-video-generate-btn" onClick={submit} disabled={busy}
        className="h-10 px-5 rounded-sm bg-forest text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-forest-soft transition-colors duration-150 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clapperboard className="h-4 w-4" />} Create promo video
      </button>
    </div>
  );
};

export const AiVideoCard = ({ v, onDelete }) => (
  <div data-testid={`ai-video-card-${v.id}`} className="border border-[#E6E4DD] bg-white rounded-sm overflow-hidden flex flex-col">
    <div className="relative aspect-video bg-forest/5 grid place-items-center overflow-hidden">
      {v.status === "completed" ? (
        <video data-testid={`ai-video-player-${v.id}`} src={fileUrl(v.id)} poster={fileUrl(v.id, "image")} controls preload="none" className="absolute inset-0 w-full h-full object-contain bg-black" />
      ) : v.status === "failed" ? (
        <div className="text-center text-red-700 text-xs px-4"><AlertTriangle className="h-5 w-5 mx-auto mb-1" />{v.error || "Generation failed"}</div>
      ) : (
        <div data-testid={`ai-video-processing-${v.id}`} className="text-center text-forest/60 text-xs"><Loader2 className="h-5 w-5 mx-auto mb-1 animate-spin" />Creating video…</div>
      )}
    </div>
    <div className="p-3 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-forest truncate">{v.title}</div>
        <div className="text-[11px] text-forest/50 truncate">{v.project_name ? `${v.project_name} · ` : ""}{v.duration}s · {v.aspect_ratio} · {v.resolution} · {new Date(v.created_at).toLocaleString()}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {v.status === "completed" && (
          <a data-testid={`ai-video-download-${v.id}`} href={fileUrl(v.id, "video", true)} className="h-8 w-8 grid place-items-center rounded-sm border border-[#E6E4DD] text-forest hover:border-forest" title="Download"><Download className="h-4 w-4" /></a>
        )}
        <button data-testid={`ai-video-delete-${v.id}`} onClick={() => onDelete(v)} className="h-8 w-8 grid place-items-center rounded-sm border border-[#E6E4DD] text-forest/70 hover:text-red-700 hover:border-red-300" title="Delete"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  </div>
);
